// frontend/src/pages/HomePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import ProductCard from '../components/ProductCard';

const BLUE_DARK   = '#1e3a8a';
const BLUE_MAIN   = '#1a56db';
const BLUE_LIGHT  = '#eff6ff';
const BLUE_ACCENT = '#3b82f6';

function SkeletonCard() {
  return (
    <div style={{ borderRadius: '16px', overflow: 'hidden', background: '#fff', boxShadow: '0 2px 12px rgba(26,86,219,0.07)', height: '320px' }}>
      <div style={{ height: '180px', background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ height: '14px', borderRadius: '8px', background: '#e2e8f0', width: '80%' }} />
        <div style={{ height: '12px', borderRadius: '8px', background: '#e2e8f0', width: '50%' }} />
        <div style={{ height: '18px', borderRadius: '8px', background: '#dbeafe', width: '40%', marginTop: '8px' }} />
      </div>
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', color: '#94a3b8' }}>
      <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#64748b', margin: '0 0 8px' }}>
        {hasFilters ? 'No hay resultados para tu búsqueda' : 'Aún no hay publicaciones'}
      </p>
      <p style={{ fontSize: '0.9rem', margin: 0 }}>
        {hasFilters ? 'Probá con otras palabras o filtros.' : '¡Sé el primero en publicar algo!'}
      </p>
    </div>
  );
}

function HomePage() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [total, setTotal]         = useState(0);
  const [universidades, setUniversidades] = useState([]);
  const [facultades, setFacultades] = useState([]);
  const [filters, setFilters]     = useState({
    q: '', selectedUniversidad: '', facultad_id: '', tipo_operacion: ''
  });

  useEffect(() => {
    api.get('/general/universidades')
      .then(r => setUniversidades(r.data))
      .catch(e => console.error('Error cargando universidades', e));
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (filters.q) params.append('q', filters.q);
      if (filters.tipo_operacion) params.append('tipo_operacion', filters.tipo_operacion);
      if (filters.facultad_id) params.append('facultad_id', filters.facultad_id);
      else if (filters.selectedUniversidad) params.append('universidad_id', filters.selectedUniversidad);
      const r = await api.get('/items/', { params });
      setItems(r.data.items); setTotal(r.data.total);
    } catch (err) {
      setError('No se pudieron cargar los productos.'); setItems([]); setTotal(0);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 500);
    return () => clearTimeout(t);
  }, [filters, fetchItems]);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleUniversidadChange = e => {
    const uniId = e.target.value;
    setFilters(prev => ({ ...prev, selectedUniversidad: uniId, facultad_id: '' }));
    if (uniId) {
      const uni = universidades.find(u => u.id === parseInt(uniId));
      setFacultades(uni ? uni.facultades : []);
    } else { setFacultades([]); }
  };

  const token = sessionStorage.getItem('token');
  const hasFilters = filters.q || filters.selectedUniversidad || filters.facultad_id || filters.tipo_operacion;

  const TIPOS = [
    { value: '', label: 'Todo', icon: '🏪' },
    { value: 'Venta', label: 'Ventas', icon: '🏷️' },
    { value: 'Préstamo', label: 'Préstamos', icon: '🔄' },
    { value: 'Servicio', label: 'Servicios', icon: '🛠️' },
  ];

  return (
    <div style={{ fontFamily: "'Poppins', 'Segoe UI', sans-serif" }}>

      {/* HERO */}
      <section style={{
        background: `linear-gradient(135deg, ${BLUE_DARK} 0%, ${BLUE_MAIN} 60%, ${BLUE_ACCENT} 100%)`,
        borderRadius: '0 0 32px 32px', padding: '56px 24px 48px', marginBottom: '40px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '10%', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <span style={{
            display: 'inline-block', background: 'rgba(255,255,255,0.15)', color: '#bfdbfe',
            fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '4px 14px', borderRadius: '20px', marginBottom: '20px',
          }}>🎓 El mercado de la comunidad universitaria</span>

          <h1 style={{ color: '#fff', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.2, margin: '0 0 14px' }}>
            Comprá, vendé y cambiá<br />
            <span style={{ color: '#93c5fd' }}>entre universitarios</span>
          </h1>

          <p style={{ color: '#bfdbfe', fontSize: '1rem', marginBottom: '32px', lineHeight: 1.6 }}>
            Libros, apuntes, equipos y servicios — directo de tu facultad.
          </p>

          {/* Barra de búsqueda */}
          <div style={{
            display: 'flex', gap: '10px', background: 'rgba(255,255,255,0.12)',
            borderRadius: '16px', padding: '10px', backdropFilter: 'blur(8px)',
            flexWrap: 'wrap', justifyContent: 'center',
          }}>
            <div style={{ position: 'relative', flex: '1 1 260px' }}>
              <svg style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text" name="q"
                placeholder="Buscar libros, apuntes, servicios..."
                value={filters.q} onChange={handleFilterChange}
                style={{ width: '100%', padding: '12px 16px 12px 44px', border: 'none', borderRadius: '10px', fontSize: '0.95rem', background: '#fff', boxSizing: 'border-box', outline: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </div>

            <select name="selectedUniversidad" value={filters.selectedUniversidad} onChange={handleUniversidadChange}
              style={{ flex: '0 1 150px', padding: '12px 14px', border: 'none', borderRadius: '10px', fontSize: '0.9rem', background: '#fff', outline: 'none', color: filters.selectedUniversidad ? '#1e3a8a' : '#94a3b8', cursor: 'pointer' }}>
              <option value="">Universidad</option>
              {universidades.map(u => <option key={u.id} value={u.id}>{u.siglas || u.nombre}</option>)}
            </select>

            <select name="facultad_id" value={filters.facultad_id} onChange={handleFilterChange}
              disabled={!filters.selectedUniversidad}
              style={{ flex: '0 1 150px', padding: '12px 14px', border: 'none', borderRadius: '10px', fontSize: '0.9rem', background: '#fff', outline: 'none', color: filters.facultad_id ? '#1e3a8a' : '#94a3b8', cursor: filters.selectedUniversidad ? 'pointer' : 'not-allowed', opacity: filters.selectedUniversidad ? 1 : 0.6 }}>
              <option value="">Facultad</option>
              {facultades.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
          </div>

          {!token && (
            <p style={{ marginTop: '20px', color: '#bfdbfe', fontSize: '0.9rem' }}>
              ¿Tenés algo para vender o un servicio para ofrecer?{' '}
              <Link to="/register" style={{ color: '#fff', fontWeight: 600, textDecoration: 'underline' }}>Creá tu cuenta gratis</Link>
            </p>
          )}
          {token && (
            <div style={{ marginTop: '20px' }}>
              <Link to="/publicar" style={{
                display: 'inline-block', background: '#fff', color: BLUE_MAIN, fontWeight: 700,
                padding: '10px 28px', borderRadius: '10px', textDecoration: 'none', fontSize: '0.95rem',
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              }}>+ Publicar ahora</Link>
            </div>
          )}
        </div>
      </section>

      {/* RESULTADOS */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 48px' }}>

        {/* Filtros de tipo — pills */}
        <div className="d-flex gap-2 flex-wrap mb-4">
          {TIPOS.map(t => (
            <button
              key={t.value}
              onClick={() => setFilters(prev => ({ ...prev, tipo_operacion: t.value }))}
              style={{
                padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.88rem', transition: 'all 0.15s',
                background: filters.tipo_operacion === t.value ? BLUE_MAIN : '#f1f5f9',
                color: filters.tipo_operacion === t.value ? '#fff' : '#64748b',
                boxShadow: filters.tipo_operacion === t.value ? '0 2px 8px rgba(26,86,219,0.25)' : 'none',
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
            {hasFilters ? 'Resultados de búsqueda' : 'Publicaciones Recientes'}
          </h2>
          {!loading && (
            <span style={{ background: BLUE_LIGHT, color: BLUE_MAIN, fontSize: '0.82rem', fontWeight: 600, padding: '2px 10px', borderRadius: '20px' }}>
              {total} {total === 1 ? 'publicación' : 'publicaciones'}
            </span>
          )}
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '14px 18px', borderRadius: '12px', marginBottom: '24px' }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))', gap: '12px' }}>
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            : items.map(item => <ProductCard key={item.id} item={item} />)
          }
        </div>

        {!loading && items.length === 0 && !error && <EmptyState hasFilters={!!hasFilters} />}
      </div>
    </div>
  );
}

export default HomePage;
